import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableContainer, Pagination, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormControl, TextField, Select, MenuItem } from '@mui/material';
import { getPermissionsList } from 'Permission/Domain/Services/GetPermissionsListService';
import { deletePermission } from 'Permission/Domain/Services/DeletePermissionService';
import { Link as RouterLink } from 'react-router-dom';
import { Delete, Edit, Restore } from '@mui/icons-material';
import { toast } from "react-toastify";
import { patchPermission } from 'Permission/Domain/Services/PatchPermissionService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { PRIVATE_ADMIN_FORM_PERMISSION } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';

function TablePermissionView() {
  const { t } = useTranslation();
  const [sendingApiRequest, setSendingApiRequest] = React.useState(false);
  const limit = 10;
  const [offset, setOffset] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [permissions, setPermissions] = React.useState([]);
  const [permissionsRows, setPermissionsRows] = React.useState([]);
  const [loadPermissions, setLoadPermissions] = React.useState(true);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [filterSku, setFilterSku] = React.useState('');
  const [filterDescription, setFilterDescription] = React.useState('');
  const [filterCategory, setFilterCategory] = React.useState('');
  const [filterDeleted, setFilterDeleted] = React.useState(0);
  const [openRestoreModal, setOpenRestoreModal] = React.useState(false);

  const handleConfirmRestore = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      patchPermission(selectedId, {
        restore: true
      }).then((response) => {
        if (response) {
          setOpenRestoreModal(false);
          setLoadPermissions(true);
          setSelectedId(null);
          toast.success(t('permission.table.modal.restore.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  const handleRestore = (userId) => {
    setSelectedId(userId);
    setOpenRestoreModal(true);
  };

  const handleFilterSkuChange = (event) => {
    setFilterSku(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissions(true);
  };

  const handleFilterDescriptionChange = (event) => {
    setFilterDescription(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissions(true);
  };

  const handleFilterCategoryChange = (event) => {
    setFilterCategory(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissions(true);
  };

  const handleFilterDeletedChange = (event) => {
    setFilterDeleted(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissions(true);
  };

  // Fetch permission data on mount or when loadPermissions flag changes
  React.useEffect(() => {
    if (loadPermissions) {
      getPermissionsList({
        offset: offset,
        limit: limit,
        filterSku: filterSku,
        filterDescription: filterDescription,
        filterCategory: filterCategory,
        filterDeleted: filterDeleted
      }).then((response) => {
        setPermissions(response.records);
        setLastPage(Math.max(1, Math.ceil(response.total / limit)));
        setLoadPermissions(false);
      });
    }
  }, [loadPermissions, offset, limit, filterSku, filterDescription, filterCategory, filterDeleted]);

  const handleDelete = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      deletePermission(selectedId).then((response) => {
        if (response) {
          setOpenDeleteModal(false);
          setLoadPermissions(true);
          setSelectedId(null);
          toast.success(t('permission.table.modal.delete.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  // Update table rows based on permissions
  React.useEffect(() => {
    const rows = permissions.map((permission) => (
      <TableRow key={permission.sku}>
        <TableCell align='center'>{permission.sku}</TableCell>
        <TableCell align='center'>{permission.description}</TableCell>
        <TableCell align='center'>
          <span
            style={{
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              padding: '4px 10px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '0.82rem'
            }}
          >
            {permission.category || 'GENERAL'}
          </span>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {/* Add actions for each permission here */}
          {permission.deletedAt === null ? (<>
            <Button variant="contained" color="error" onClick={() => handleDelete(permission.sku)}>
              <Delete />
            </Button>
            <Button variant="contained" color="primary" component={RouterLink} to={`${PRIVATE_ADMIN_FORM_PERMISSION}/${permission.sku}`}>
              <Edit />
            </Button>
            </>) : (
            <Button variant="contained" color="success" onClick={() => handleRestore(permission.sku)}>
              <Restore />
            </Button>
          )}
          </Box>
        </TableCell>
      </TableRow>
    ));
    setPermissionsRows(rows);
  }, [permissions]);

  // Update offset when page changes
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    setOffset((newPage - 1) * limit);
    setLoadPermissions(true);
  };

  return (
    <>
      <Paper
          sx={{
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          with: "100%"
          }}
      >
          <Box sx={{ p: 2, flex: 1 }}>
              <Box sx={{ marginBottom: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permission.table.filter_by_sku')}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={filterSku}
                        onChange={handleFilterSkuChange}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permission.table.filter_by_description')}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={filterDescription}
                        onChange={handleFilterDescriptionChange}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permission.table.filter_by_category')}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={filterCategory}
                        onChange={handleFilterCategoryChange}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permission.table.filter_by_deleted')}
                      </Typography>
                      <Select
                        size="small"
                        value={filterDeleted}
                        onChange={handleFilterDeletedChange}
                      >
                        <MenuItem value={0}>{t('permission.table.filter_by_deleted_not_deleted')}</MenuItem>
                        <MenuItem value={1}>{t('permission.table.filter_by_deleted_deleted')}</MenuItem>
                        <MenuItem value={2}>{t('permission.table.filter_by_deleted_all')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" component={RouterLink} to={PRIVATE_ADMIN_FORM_PERMISSION + '/new'}>{t('permission.table.button.create')}</Button>
              </Box>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center"}}>{t('permission.table.title')}</Typography>
              <TableContainer component={Paper}>
              <Table sx={{ minWidth: "100%" }} aria-label="channels table">
                  <TableHead>
                  <TableRow>
                      <TableCell align='center'>{t('permission.table.sku')}</TableCell>
                      <TableCell align='center'>{t('permission.table.description')}</TableCell>
                      <TableCell align='center'>{t('permission.table.category')}</TableCell>
                      <TableCell align="center">{t('permission.table.actions')}</TableCell>
                  </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadPermissions ? (
                      <TableRow>
                        <TableCell colSpan={100} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : (
                      permissionsRows
                    )}
                  </TableBody>
              </Table>
              </TableContainer>

              {/* Add paginator component */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Pagination count={lastPage} page={currentPage} onChange={handlePageChange} />
              </Box>

              {/* Delete confirmation modal */}
              <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <DialogTitle>{t('permission.table.modal.delete.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('permission.table.modal.delete.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenDeleteModal(false)}>{t('permission.table.modal.delete.button.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmDelete}>{t('permission.table.modal.delete.button.delete')}</Button>
                </DialogActions>
              </Dialog>

              {/* Restore confirmation modal */}
              <Dialog open={openRestoreModal} onClose={() => setOpenRestoreModal(false)}>
                <DialogTitle>{t('permission.table.modal.restore.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('permission.table.modal.restore.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenRestoreModal(false)}>{t('permission.table.modal.restore.button.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmRestore}>{t('permission.table.modal.restore.button.restore')}</Button>
                </DialogActions>
              </Dialog>

          </Box>
          <DialogLoader open={sendingApiRequest} />
      </Paper>
    </>
  );
}

export default TablePermissionView;
